import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Web3Provider } from "./providers/Web3Provider";
import { WebSocketProvider } from "./providers/WebSocketProvider";
import { Toaster } from "./components/ui/sonner";

// Pages - Core (Simplified)
import { Landing } from "./pages/Landing";
import { Login } from "./pages/Login";
import { Register } from "./pages/Register";
import { Onboarding } from "./pages/Onboarding";
import { Treasury } from "./pages/Treasury";
import { Settings } from "./pages/Settings";
import { Referrals } from "./pages/Referrals";
import { VPSControl } from "./pages/VPSControl";
import { AdminOps } from "./pages/AdminOps";
import { AdminOpsBacktest } from "./pages/AdminOpsBacktest";
import { AdminOpsGovernor } from "./pages/AdminOpsGovernor";
import { Strategies } from "./pages/Strategies";
import { Packages } from "./pages/Packages";
import { MyInvestments } from "./pages/MyInvestments";

// Layout
import { DashboardLayout } from "./components/layout/DashboardLayout";

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Onboarding Route wrapper
const OnboardingRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (user?.onboarding_completed) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Public Route (redirect if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/admin/ops" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      
      {/* Onboarding */}
      <Route path="/onboarding" element={<OnboardingRoute><Onboarding /></OnboardingRoute>} />
      
      {/* Protected dashboard routes - Simplified */}
      <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<Navigate to="/admin/ops" replace />} />
        <Route path="/my-investments" element={<MyInvestments />} />
        <Route path="/packages" element={<Packages />} />
        <Route path="/vps-control" element={<VPSControl />} />
        <Route path="/referrals" element={<Referrals />} />
        <Route path="/treasury" element={<Treasury />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/admin" element={<Navigate to="/admin/ops" replace />} />
        <Route path="/admin/ops" element={<AdminOps />} />
        <Route path="/admin/ops/backtest" element={<AdminOpsBacktest />} />
        <Route path="/admin/ops/governor" element={<AdminOpsGovernor />} />
        <Route path="/admin/ops/strategies" element={<Strategies />} />
      </Route>
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App dark">
      <Web3Provider>
        <BrowserRouter>
          <AuthProvider>
            <WebSocketProvider>
              <AppRoutes />
              <Toaster position="top-right" richColors />
            </WebSocketProvider>
          </AuthProvider>
        </BrowserRouter>
      </Web3Provider>
    </div>
  );
}

export default App;
